// src/SallerAdmin/pages/Leads/LeadsPage.tsx

import React, { useState, useEffect } from 'react';
import { 
    Search, MessageSquare, Clock, Package, Mail, Phone, 
    Calendar, MessageCircle, CheckCircle, AlertCircle, X, 
    Filter, ChevronRight 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// --- TOAST NOTIFICATION COMPONENT ---
const Toast = ({ show, message, type, onClose }: any) => {
    if (!show) return null;
    const isSuccess = type === 'success';
    return (
        <div className={`fixed top-6 left-1/2 transform -translate-x-1/2 z-[110] flex items-center gap-3 px-5 py-3 rounded-lg shadow-xl border animate-in slide-in-from-top-5 fade-in duration-300 ${isSuccess ? 'bg-white border-green-200 text-green-800' : 'bg-white border-red-200 text-red-800'}`}>
            <div className={`p-1.5 rounded-full ${isSuccess ? 'bg-green-100' : 'bg-red-100'}`}>
                {isSuccess ? <CheckCircle size={18} className="text-green-600"/> : <AlertCircle size={18} className="text-red-600"/>}
            </div>
            <div>
                <h4 className="text-sm font-bold">{isSuccess ? 'Success' : 'Error'}</h4>
                <p className="text-xs font-medium opacity-90">{message}</p>
            </div>
            <button onClick={onClose} className="ml-4 p-1 hover:bg-black/5 rounded-full"><X size={14}/></button>
        </div>
    );
};

// --- TYPES ---
interface Lead {
    conversationId: number;
    buyerId: number;
    buyerName: string;
    buyerEmail: string;
    productName: string;
    productId: number;
    lastMessage: string;
    lastMessageTime: string;
    unreadCount: number;
}

interface Inquiry {
    id: number;
    buyer_id: number | null;
    name: string;
    email: string;
    mobile: string;
    message: string;
    quantity: number;
    created_at: string;
    product_id: number;
    productName: string;
    productImage: string;
}

const LeadsPage: React.FC = () => {
    const navigate = useNavigate();
    
    // --- STATE ---
    const [activeTab, setActiveTab] = useState<'chats' | 'inquiries'>('chats'); 
    const [leads, setLeads] = useState<Lead[]>([]); 
    const [inquiries, setInquiries] = useState<Inquiry[]>([]); 
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const userStr = localStorage.getItem('currentUser'); 
    const seller = userStr ? JSON.parse(userStr) : null;

    const showToast = (message: string, type: 'success' | 'error' = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast(prev => ({ ...prev, show: false })), 3000);
    };

    // --- FETCH DATA ---
    useEffect(() => {
        const fetchData = async () => {
            if (!seller || !seller.id) {
                setLoading(false);
                return;
            }

            try {
                // 1. Fetch Chat Leads
                const chatRes = await fetch(`/nodejs/api/chat/leads/${seller.id}`);
                const chatData = await chatRes.json();
                if (Array.isArray(chatData)) setLeads(chatData);

                // 2. Fetch Inquiries
                const inqRes = await fetch(`/nodejs/api/inquiry/seller/${seller.id}`);
                if (inqRes.ok) {
                    const inqData = await inqRes.json();
                    if (Array.isArray(inqData)) setInquiries(inqData);
                }

            } catch (err) {
                console.error("Error fetching data:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        const interval = setInterval(fetchData, 5000);
        return () => clearInterval(interval);

    }, [seller?.id]);

    // --- FILTERING ---
    const filteredLeads = leads.filter(lead => 
        (lead.buyerName && lead.buyerName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (lead.productName && lead.productName.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const filteredInquiries = inquiries.filter(inq => 
        (inq.name && inq.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (inq.email && inq.email.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // --- ACTIONS ---
    const openChat = (lead: Lead) => {
        navigate(`/chat/${lead.conversationId}?buyerId=${lead.buyerId}&buyerName=${encodeURIComponent(lead.buyerName)}&productName=${encodeURIComponent(lead.productName)}`);
    };

    const handleInquiryChat = async (inq: Inquiry) => {
        if (!inq.buyer_id) {
            showToast("Guest User: Contact via Email/Phone only.", "error");
            return;
        }

        try {
            const res = await fetch(`/nodejs/api/chat/conversation`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    buyerId: inq.buyer_id,
                    sellerId: seller.id,
                    productId: inq.product_id
                })
            });

            const data = await res.json();
            if (data.conversationId) {
                navigate(`/chat/${data.conversationId}?buyerId=${inq.buyer_id}&buyerName=${encodeURIComponent(inq.name)}&productName=${encodeURIComponent(inq.productName)}&productImage=${encodeURIComponent(inq.productImage || '')}`);
            }
        } catch (error) {
            showToast("Could not start chat. Server error.", "error");
        }
    };

    const formatTime = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    if (!seller) return <div className="p-10 text-center text-gray-500">Access Denied</div>;
    if (loading) return <div className="p-10 text-center text-[#00635e] font-bold animate-pulse">Loading Leads...</div>;

    return (
        <div className="bg-gray-50/50 min-h-screen font-sans relative">
            
            <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />

            {/* --- HEADER --- */}
            <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
                <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-xl md:text-2xl font-black text-[#00635e] flex items-center gap-2">
                                <MessageSquare className="text-red-600" /> Leads & Inquiries
                            </h1>
                            <p className="text-xs text-gray-500 mt-1">Manage buyer conversations and product inquiries.</p>
                        </div>

                        {/* Controls: Search & Filter */}
                        <div className="flex gap-3 w-full md:w-auto">
                            <div className="relative flex-1 md:w-64">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                <input 
                                    type="text" 
                                    placeholder="Search buyer or product..." 
                                    value={searchTerm} 
                                    onChange={(e) => setSearchTerm(e.target.value)} 
                                    className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-[#00635e] focus:border-[#00635e] outline-none transition-all bg-gray-50 focus:bg-white"
                                />
                            </div>
                            <button className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600 bg-white">
                                <Filter size={18} />
                            </button>
                        </div>
                    </div>

                    {/* --- TABS --- */}
                    <div className="flex gap-6 mt-6 border-b border-gray-100">
                        <button 
                            onClick={() => setActiveTab('chats')} 
                            className={`pb-3 text-sm font-bold flex items-center gap-2 border-b-2 transition-all ${activeTab === 'chats' ? 'border-[#00635e] text-[#00635e]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            <MessageSquare size={16} /> Live Chats 
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${activeTab === 'chats' ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-600'}`}>{leads.length}</span>
                        </button>
                        <button 
                            onClick={() => setActiveTab('inquiries')} 
                            className={`pb-3 text-sm font-bold flex items-center gap-2 border-b-2 transition-all ${activeTab === 'inquiries' ? 'border-[#00635e] text-[#00635e]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                        >
                            <Mail size={16} /> Product Inquiries 
                            <span className={`text-[10px] px-2 py-0.5 rounded-full ${activeTab === 'inquiries' ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-600'}`}>{inquiries.length}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* --- CONTENT AREA --- */}
            <div className="max-w-7xl mx-auto p-4 md:p-6 pb-20">
                
                {/* VIEW 1: CHATS LIST */}
                {activeTab === 'chats' && (
                    <div className="flex flex-col gap-3">
                        {filteredLeads.map((lead) => (
                            <div key={lead.conversationId} onClick={() => openChat(lead)} className="group bg-white p-4 rounded-xl border border-gray-200 hover:border-teal-200 hover:shadow-md cursor-pointer flex items-center justify-between transition-all">
                                <div className="flex items-center gap-4">
                                    <div className="relative">
                                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-[#00635e] text-white flex items-center justify-center font-bold text-lg shadow-sm">
                                            {lead.buyerName ? lead.buyerName.charAt(0).toUpperCase() : 'B'}
                                        </div>
                                        {lead.unreadCount > 0 && <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white"></span>}
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="text-sm font-bold text-gray-900 truncate flex items-center gap-2">
                                            {lead.buyerName}
                                            {lead.unreadCount > 0 && <span className="bg-red-50 text-red-600 text-[9px] px-1.5 py-0.5 rounded border border-red-100">NEW</span>}
                                        </h3>
                                        <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
                                            <Package size={12} className="text-teal-600" /> 
                                            <span className="truncate max-w-[150px] md:max-w-xs font-medium">{lead.productName}</span>
                                        </div>
                                        <p className="text-xs text-gray-400 mt-1 truncate max-w-[200px] italic">
                                            {lead.lastMessage ? `"${lead.lastMessage}"` : 'Start conversation...'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex flex-col items-end gap-2 pl-4">
                                    <span className="text-[10px] font-semibold text-gray-400 flex items-center bg-gray-50 px-2 py-1 rounded">
                                        <Clock size={10} className="mr-1" />
                                        {lead.lastMessageTime ? new Date(lead.lastMessageTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                                    </span>
                                    <ChevronRight className="text-gray-300 group-hover:text-[#00635e] transition-colors" size={18} />
                                </div>
                            </div>
                        ))}
                        {filteredLeads.length === 0 && (
                            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                                <MessageSquare size={48} className="mb-3 opacity-20" />
                                <p>No active chats found.</p>
                            </div>
                        )}
                    </div>
                )}

                {/* VIEW 2: INQUIRIES GRID */}
                {activeTab === 'inquiries' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-in fade-in duration-300">
                        {filteredInquiries.length > 0 ? (
                            filteredInquiries.map((inq) => (
                                <div key={inq.id} className="bg-white rounded-xl border border-gray-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col h-full">
                                    {/* Card Header */}
                                    <div className="p-4 border-b border-gray-50 flex justify-between items-start">
                                        <div className="flex gap-3">
                                            <div className="w-9 h-9 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center font-bold border border-orange-100">
                                                <Mail size={16} />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-sm text-gray-800 line-clamp-1">{inq.name}</h3>
                                                <p className="text-[10px] text-gray-400 flex items-center mt-0.5">
                                                    <Calendar size={10} className="mr-1" /> {formatTime(inq.created_at)}
                                                </p>
                                            </div>
                                        </div>
                                        <span className="bg-teal-50 text-teal-700 text-[10px] font-bold px-2 py-1 rounded border border-teal-100 whitespace-nowrap">
                                            Qty: {inq.quantity}
                                        </span>
                                    </div>

                                    {/* Card Body */}
                                    <div className="p-4 flex-1 flex flex-col gap-3">
                                        {/* Product Info */}
                                        <div className="flex items-start gap-2 bg-gray-50 p-2.5 rounded-lg">
                                            <Package size={14} className="text-gray-400 mt-0.5 shrink-0" />
                                            <p className="text-xs font-semibold text-gray-700 line-clamp-2">{inq.productName}</p>
                                        </div>

                                        {/* Message Preview */}
                                        <div className="relative">
                                            <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-gray-200 rounded-full"></div>
                                            <p className="text-xs text-gray-500 italic pl-3 line-clamp-2">
                                                "{inq.message}"
                                            </p>
                                        </div>

                                        {/* Contact Details */}
                                        <div className="mt-auto pt-2 space-y-1.5 border-t border-dashed border-gray-100">
                                            <div className="flex items-center gap-2 text-xs text-gray-600">
                                                <Mail size={12} className="text-gray-400" /> 
                                                <span className="truncate">{inq.email}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs text-gray-600">
                                                <Phone size={12} className="text-gray-400" /> 
                                                <span>{inq.mobile}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Buttons (Compact) */}
                                    <div className="p-3 bg-gray-50/50 border-t border-gray-100 flex gap-2">
                                        <a 
                                            href={`tel:${inq.mobile}`} 
                                            className="flex-1 flex items-center justify-center gap-1.5 bg-white border border-gray-200 text-gray-700 py-2 rounded-lg text-xs font-bold hover:bg-gray-50 transition shadow-sm"
                                        >
                                            <Phone size={14} /> Call
                                        </a>

                                        {inq.buyer_id ? (
                                            <button 
                                                onClick={() => handleInquiryChat(inq)}
                                                className="flex-1 flex items-center justify-center gap-1.5 bg-[#00635e] text-white py-2 rounded-lg text-xs font-bold hover:bg-teal-800 transition shadow-sm shadow-teal-100"
                                            >
                                                <MessageCircle size={14} /> Chat
                                            </button>
                                        ) : (
                                            <a 
                                                href={`mailto:${inq.email}`} 
                                                className="flex-1 flex items-center justify-center gap-1.5 bg-white border border-gray-200 text-blue-600 py-2 rounded-lg text-xs font-bold hover:bg-blue-50 transition shadow-sm"
                                            >
                                                <Mail size={14} /> Email
                                            </a>
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="col-span-full flex flex-col items-center justify-center py-20 text-gray-400 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/30">
                                <Mail size={40} className="mb-3 opacity-20" />
                                <p className="text-sm font-medium">No inquiries found.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LeadsPage;